const STORAGE_KEY = 'rtt_signup_attribution_v1';

export type SignupAttributionSnapshot = {
  surface: 'booking';
  entry_path: string;
  route_zone: 'public' | 'embed';
  pro_slug: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer: string | null;
};

export type BookingOrigin = 'embed' | 'pro_link' | 'marketplace' | 'direct';

const MARKETPLACE_EXACT = new Set([
  '/',
  '/recherche',
  '/services',
  '/blog',
  '/annuaire',
  '/comment-ca-marche',
  '/fonctionnalites',
  '/about',
]);

function proSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/(?:embed\/(?:booking|services)|pro|pro-old)\/([^/]+)/);
  const slug = match?.[1]?.trim();
  if (!slug || !/^[A-Za-z0-9][A-Za-z0-9_-]{0,120}$/.test(slug)) return null;
  return slug;
}

/** Même règle que le serveur, sans invitation ni compte pro. */
export function bookingOriginFromPath(
  pathname: string,
  utmSource?: string | null
): BookingOrigin {
  if (pathname.startsWith('/embed')) return 'embed';
  if (proSlugFromPath(pathname) && (pathname.startsWith('/pro/') || pathname.startsWith('/pro-old/'))) {
    return 'pro_link';
  }
  if (
    MARKETPLACE_EXACT.has(pathname) ||
    pathname.startsWith('/services/') ||
    pathname.startsWith('/blog/') ||
    pathname.startsWith('/annuaire/') ||
    pathname.startsWith('/fonctionnalites/') ||
    (utmSource || '').toLowerCase() === 'rintintin'
  ) {
    return 'marketplace';
  }
  return 'direct';
}

export function bookingPathContext(pathname: string, utmSource?: string | null) {
  const proSlug = proSlugFromPath(pathname);
  return {
    page_origin: bookingOriginFromPath(pathname, utmSource),
    ...(proSlug ? { pro_slug: proSlug } : {}),
  };
}

/** Première page de la session. Les pages suivantes ne réécrivent pas. */
export function rememberBookingLanding(pathname: string, search: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(STORAGE_KEY)) return;
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const snapshot: SignupAttributionSnapshot = {
      surface: 'booking',
      entry_path: pathname.slice(0, 512),
      route_zone: pathname.startsWith('/embed') ? 'embed' : 'public',
      pro_slug: proSlugFromPath(pathname),
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      referrer: (document.referrer || '').split('?')[0].slice(0, 512) || null,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* Le stockage ne doit pas bloquer la navigation. */
  }
}

export function readSignupAttribution(): SignupAttributionSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SignupAttributionSnapshot;
    if (!parsed || parsed.surface !== 'booking' || typeof parsed.entry_path !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function signupAttributionBody(): { signup_attribution?: SignupAttributionSnapshot } {
  const snapshot = readSignupAttribution();
  return snapshot ? { signup_attribution: snapshot } : {};
}

/** Query à coller sur le callback Google (GET). */
export function signupAttributionQueryString(): string {
  const snapshot = readSignupAttribution();
  if (!snapshot) return '';
  const params = new URLSearchParams();
  const set = (key: string, value: string | null | undefined) => {
    const text = value?.trim();
    if (text) params.set(key, text);
  };
  set('signup_surface', snapshot.surface);
  set('signup_entry_path', snapshot.entry_path);
  set('signup_route_zone', snapshot.route_zone);
  set('signup_pro_slug', snapshot.pro_slug);
  set('signup_utm_source', snapshot.utm_source);
  set('signup_utm_medium', snapshot.utm_medium);
  set('signup_utm_campaign', snapshot.utm_campaign);
  set('signup_referrer', snapshot.referrer);
  const encoded = params.toString();
  return encoded ? `&${encoded}` : '';
}
